import React, { useState } from "react";
import { api } from "../lib/api";
import JobListings from "./JobListings";
import RoleCard, { fmt } from "./RoleCard";
import SearchPartners from "./SearchPartners";

const MIN_LEVEL_ONE = 5;

const distanceToWindow = (band, window) => {
  if (!band?.min || !window) return Number.MAX_SAFE_INTEGER;
  if (band.max < window.low) return window.low - band.max;
  if (band.min > window.high) return band.min - window.high;
  return 0;
};

const TopRolesTarget = ({ resumeId, target, roles, onCreate, onCreateJob, generatingRoleId, compensation }) => {  const [workModel, setWorkModel] = useState("all");
  const [region, setRegion] = useState("all");
  const [salaryOnly, setSalaryOnly] = useState(false);
  const [jobs, setJobs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [salary, setSalary] = useState(null);
  const [showLevelTwo, setShowLevelTwo] = useState(false);
  const [anyRangeChoice, setAnyRangeChoice] = useState(null);

  React.useEffect(() => {
    api.get(`/resumes/${resumeId}/role-salaries`).then((r) => setSalary(r.data)).catch(() => setSalary(null));
  }, [resumeId]);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    api.get(`/resumes/${resumeId}/jobs`, { params: { work_model: workModel, region, salary_only: salaryOnly } })
      .then((r) => { if (active) setJobs(r.data); })
      .catch(() => { if (active) setError("Job feeds are temporarily unavailable. Try refreshing in a moment."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [resumeId, workModel, region, salaryOnly, retry]);

  const salaryWindow = salary?.expected_amount ? salary.window : null;
  const bandOf = (title) => (salary?.bands || []).find((b) => b.title === title);

  // Level 1 = inside the expected range, topped up to five with the closest-paying roles.
  const all = roles || [];
  const inRange = salaryWindow ? all.filter((r) => bandOf(r.title)?.verdict === "in_range") : all;
  const rest = all.filter((r) => !inRange.includes(r));
  const topUps = salaryWindow
    ? [...rest].filter((r) => bandOf(r.title)?.verdict !== "stretch")
        .sort((a, b) => distanceToWindow(bandOf(a.title), salaryWindow) - distanceToWindow(bandOf(b.title), salaryWindow))
        .slice(0, Math.max(0, MIN_LEVEL_ONE - inRange.length))
    : [];
  const levelOne = [...inRange, ...topUps];
  const levelTwo = all.filter((r) => !levelOne.includes(r));
  const higherCount = all.filter((r) => bandOf(r.title)?.verdict === "stretch").length;
  const noHigher = !!salaryWindow && higherCount === 0;
  const levelTwoOpen = showLevelTwo;

  const cardProps = (role, i, closest) => ({
    role, index: i, band: bandOf(role.title), currency: salary?.currency || "INR", closest,
    jobs: jobs?.groups?.[role.role_id], jobsLoading: loading, jobsError: error,
    onCreate, onCreateJob, generatingRoleId, salaryWindow,
  });

  return (
    <div className="card-soft p-5 h-full" data-testid="top-roles-container">
      <h3 className="text-lg font-bold text-slate-900 mb-4">Top Roles You Could Target</h3>
      <SearchPartners roles={roles} target={target} />

      <div className="grid grid-cols-2 gap-3 items-end mb-4 sm:flex sm:flex-wrap" data-testid="job-filters">
        <label className="text-xs text-slate-500 min-w-0">Working model
          <select value={workModel} onChange={(e) => setWorkModel(e.target.value)} className="block mt-1 w-full px-3 rounded-xl bg-white border border-slate-200 text-slate-800 text-base sm:text-sm" data-testid="job-work-model-filter">
            <option value="all">All work models</option><option value="remote">Remote</option><option value="hybrid">Hybrid</option><option value="onsite">Onsite</option>
          </select>
        </label>
        <label className="text-xs text-slate-500 min-w-0">Location
          <select value={region} onChange={(e) => setRegion(e.target.value)} className="block mt-1 w-full px-3 rounded-xl bg-white border border-slate-200 text-slate-800 text-base sm:text-sm" data-testid="job-region-filter">
            <option value="all">India &amp; outside India</option><option value="india">India</option><option value="international">Outside India</option>
          </select>
        </label>
        <label className="text-xs text-slate-500 flex items-center gap-2 py-2">
          <input type="checkbox" className="w-4 h-4" checked={salaryOnly} onChange={(e) => setSalaryOnly(e.target.checked)} data-testid="job-salary-filter" />Salary disclosed
        </label>
        <button disabled={loading} onClick={() => setRetry((v) => v + 1)} className="text-xs text-sky-700 py-2 text-left disabled:opacity-50" data-testid="jobs-retry-button">Refresh results</button>
      </div>

      <p className="text-[11px] text-slate-500 mb-3" data-testid="jobs-coverage-note">{jobs?.coverage || "Real vacancies from selected employer career boards and public feeds. No invented openings."}</p>
      {jobs?.sources?.some((s) => s.error) && <p className="text-xs text-amber-700 mb-3" data-testid="jobs-source-warning">Some sources are temporarily unavailable. Available feeds are still shown.</p>}
      {target?.role_title && (
        <details className="mb-4 rounded-xl border border-slate-200 p-3" data-testid="target-job-openings">
          <summary className="cursor-pointer text-sm text-sky-700" data-testid="target-job-openings-toggle">Openings for {target.role_title}</summary>
          <JobListings jobs={jobs?.groups?.__target__} roleId="target" loading={loading} error={error} onCreate={onCreateJob}
            generatingId={generatingRoleId} salaryWindow={salaryWindow} currency={salary?.currency || "INR"} />
        </details>
      )}

      {salaryWindow && (
        <div className="mb-4 accent-bar rounded-xl border border-slate-200 bg-teal-50/50 p-3 pl-4" style={{ "--accent": "#0D9488" }} data-testid="salary-match-bar">
          <p className="text-[10px] font-mono uppercase tracking-wider text-teal-700">Salary matching</p>
          <p className="mt-0.5 text-sm text-slate-800">
            Your expected <span className="num-display">{salary.currency} {fmt(salary.expected_amount)}</span> · matching window <span className="num-display">{fmt(salaryWindow.low)}–{fmt(salaryWindow.high)}</span>
          </p>
          <p className="mt-1.5 text-[11px] text-slate-500">{salary.note}</p>
        </div>
      )}

      {all.length === 0 ? (
        <p className="text-sm text-slate-500">We didn't find enough credible signal to defensibly recommend roles yet. Strengthen your resume evidence and re-run the analysis.</p>
      ) : (
        <>
          {salaryWindow && (
            <p className="mb-2 text-[11px] font-mono uppercase tracking-wider text-slate-500" data-testid="level-one-heading">
              {inRange.length === 0
                ? `Level 1 · no role sits inside your range yet — showing the ${topUps.length} closest`
                : `Level 1 · inside your expected range${topUps.length ? ` (plus the ${topUps.length} closest)` : ""}`}
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 items-stretch" data-testid="level-one-roles">
            {levelOne.map((role, i) => <RoleCard key={role.role_id} {...cardProps(role, i, topUps.includes(role))} />)}
          </div>

          {(levelTwo.length > 0 || noHigher) && (
            <div className="mt-4" data-testid="level-two-wrap">
              {noHigher && anyRangeChoice === null ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5" data-testid="no-higher-salary-prompt">
                  <p className="text-sm text-slate-800">No higher salary range for this position is available in our website or partner websites. Do you want to check the job availability at any salary range?</p>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => { setAnyRangeChoice("yes"); setShowLevelTwo(true); }} className="h-10 rounded-xl bg-sky-600 px-5 text-sm font-semibold text-white hover:bg-sky-700" data-testid="any-range-yes">Yes</button>
                    <button onClick={() => setAnyRangeChoice("no")} className="h-10 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 hover:border-slate-300" data-testid="any-range-no">No</button>
                  </div>
                </div>
              ) : anyRangeChoice === "no" ? (
                <button onClick={() => setAnyRangeChoice(null)} className="text-xs font-semibold text-sky-700 hover:text-sky-800" data-testid="any-range-reopen">Changed your mind? Check roles at any salary range</button>
              ) : levelTwo.length === 0 ? (
                <p className="text-xs text-slate-500" data-testid="level-two-empty">We have no other role at a different salary range for this profile right now.</p>
              ) : (
                <>
                  <button onClick={() => setShowLevelTwo((v) => !v)} className="text-xs font-semibold text-sky-700 hover:text-sky-800" data-testid="level-two-toggle">
                    {levelTwoOpen ? "Hide" : "Show"} level 2 · {higherCount > 0 ? `${higherCount} higher-paying + ${levelTwo.length - higherCount} other role${levelTwo.length - higherCount === 1 ? "" : "s"}` : `${levelTwo.length} role${levelTwo.length === 1 ? "" : "s"}`} at other salary ranges
                  </button>
                  {levelTwoOpen && (
                    <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 items-stretch" data-testid="level-two-roles">
                      {levelTwo.map((role, i) => <RoleCard key={role.role_id} {...cardProps(role, levelOne.length + i, false)} />)}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TopRolesTarget;
